/**
 * Admin: Mailboxes (SA-92, spec §9).
 *
 * Ingests inbound-only group mailboxes (Gmail / Microsoft 365) and WhatsApp
 * group sources into a workflow, a record, a staff conversation or a router.
 * SA-1387: the mailbox list beside a pane. The pane shows one mailbox (its
 * Overview, Routing and Triage tabs), the New mailbox form or Provider
 * settings; the address names it (`mailbox`, `tab`, `pane`), every write a
 * replace. This file owns the address, the reads, the mutations and their
 * handlers; the pieces live in `./mailboxes/`. Spec §5.7: while the routing
 * editor holds unsaved edits, anything that would drop them asks first, and
 * so does leaving the page.
 */

import { PageFrame } from '@/components/layout/PageFrame'
import { tourAnchor } from '@/components/tour/tourAnchor'
import { PageHeader } from '@/components/layout/PageHeader'
import { useEffect, useRef, useState } from 'react'
import { NavigationType, useSearchParams, type BlockerFunction } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { Alert, AlertDescription, Button, Spinner } from '@/components/ui'
import { Mail, Plus } from 'lucide-react'
import { usePersonas } from '@/hooks'
import { useUnsavedChangesGuard } from '@/hooks/useUnsavedChangesGuard'
import { useWorkflowsLibrary } from '@/hooks/useWorkflows'
import { ApiError } from '@/services/apiClient'
import { useFeatureStore } from '@/stores/featureStore'
import { cn } from '@/utils'
import { mailboxesService, type Mailbox } from '@/services/mailboxes'
import { BackloadDialog } from './mailboxes/BackloadDialog'
import { ChangeAccountDialog } from './mailboxes/ChangeAccountDialog'
import { backloadErrorText } from './mailboxes/backloadText'
import { MailboxList } from './mailboxes/MailboxList'
import { MailboxOverview } from './mailboxes/MailboxOverview'
import { MailboxPane } from './mailboxes/MailboxPane'
import { MailboxRouting } from './mailboxes/MailboxRouting'
import { NewMailboxForm } from './mailboxes/NewMailboxForm'
import { ProviderSettings } from './mailboxes/ProviderSettings'
import { useAdminUsers, useIntakeAgendas, useProviderConfigs } from './mailboxes/useMailboxPageData'
import { TriageMetricsPanel } from './mailboxes/TriageMetricsPanel'
import { connectErrorText, refusalAction, refusalDetail } from './mailboxes/connectErrorText'
import { DELETE_ANYWAY_CONFIRM, deleteConfirmText, isGroupSource } from './mailboxes/groupSourceText'
import { WahaConnectDialog } from './mailboxes/WahaConnectDialog'
import {
  attentionLine, groupMailboxes, landingParams, paneFrom, providerNeedsClient, withPane,
  type MailboxTab, type PaneTarget,
} from './mailboxes/mailboxListModel'
import {
  ACCOUNT_CHANGED, CHANGE_ACCOUNT, CONFIRM_FAILED, EMPTY_LIST, NEW_MAILBOX, PROVIDER_SETTINGS,
  PROVIDER_SETTINGS_MISSING, UNKNOWN, connectAsLabel, discardRoutingConfirm, emailDeleteConfirm, errorMessage,
  isPolicyChanged, sinkLabel,
} from './mailboxes/mailboxesText'
import { type PolicyDoc } from './mailboxes/routerPolicyModel'
import {
  MAILBOXES_KEY,
  useConfirmMailboxAccount,
  useConnectMailbox,
  useCancelBackload,
  useDeleteMailbox,
  useMailboxHandlers,
  useMailboxPolicyChanges,
  useMailboxProviders,
  useMailboxes,
  usePauseMailbox,
  usePollMailboxNow,
  useResumeMailbox,
  useUpdateMailbox,
} from '@/hooks/useMailboxes'

/** Spec §5.5: below this container width the list and the pane stack. */
const STACK_BELOW_REM = 40

/**
 * Spec §5.7: every write the page makes is a replace on its own path, so only those pass; anything else
 * blocks, a push onto this path too (the admin area's active Mailboxes row would reset the pane).
 */
const notAPageWrite: BlockerFunction = ({ currentLocation, nextLocation, historyAction }) =>
  currentLocation.pathname !== nextLocation.pathname || historyAction !== NavigationType.Replace

/** SA-1422: a refused connect, kept from the OAuth callback's landing until an action clears it. */
interface Landing {
  code: string
  params: { expected: string | null; account: string | null; basis: string | null }
  /** The id of the mailbox that already reads the account (`account_in_use`). */
  other: string | null
  /** The mailbox the refusal was for; its action is offered only while the pane shows it. */
  mailboxId: string | null
}

export function MailboxesPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const qc = useQueryClient()

  // SA-769: the OAuth callback lands here with `oauth_error` when a connect
  // failed or was refused. Keep it once for the banner, then drop the
  // parameters (replace) so a reload does not repeat it. SA-755: a mismatch
  // also carries `expected` and `account`, dropped with it. SA-1387: a
  // connect that succeeded names its mailbox in `connected`, which becomes
  // `mailbox` so the person lands on it. SA-1422: a refusal also carries
  // `basis` and `other` (dropped) and the `mailbox` it was for, which is the
  // pane key and stays. The banner is worded at render, so `other` is named
  // from the list once it has loaded.
  const [landing, setLanding] = useState<Landing | null>(null)
  const oauthError = searchParams.get('oauth_error')
  const oauthExpected = searchParams.get('expected')
  const oauthAccount = searchParams.get('account')
  const oauthBasis = searchParams.get('basis')
  const oauthOther = searchParams.get('other')
  const connected = searchParams.get('connected')
  const landedOn = searchParams.get('mailbox')
  useEffect(() => {
    if ([oauthError, oauthExpected, oauthAccount, oauthBasis, oauthOther, connected].every((v) => v === null)) return
    if (oauthError !== null) {
      setLanding({
        code: oauthError,
        params: { expected: oauthExpected, account: oauthAccount, basis: oauthBasis },
        other: oauthOther,
        mailboxId: landedOn,
      })
    }
    setSearchParams((prev) => landingParams(prev)?.next ?? prev, { replace: true })
    // A later pane move changes `landedOn` with every landing key gone, so it lands nothing.
  }, [oauthError, oauthExpected, oauthAccount, oauthBasis, oauthOther, connected, landedOn, setSearchParams])

  const query = useMailboxes()
  const mailboxes = query.data
  const connectMutation = useConnectMailbox()
  const confirmMutation = useConfirmMailboxAccount()
  const pauseMutation = usePauseMailbox()
  const resumeMutation = useResumeMailbox()
  const pollMutation = usePollMailboxNow()
  const deleteMutation = useDeleteMailbox()
  const updateMutation = useUpdateMailbox()
  const cancelBackloadMutation = useCancelBackload()
  // SA-759: the mailbox whose "Load earlier mail" dialog is open.
  const [backloadFor, setBackloadFor] = useState<Mailbox | null>(null)
  // SA-1422: the mailbox whose Change account dialog is open, and the account a refusal offered.
  const [changeFor, setChangeFor] = useState<{ mailbox: Mailbox; initialAccount?: string } | null>(null)

  /** A listed mailbox's display name, for a refusal that names another mailbox by id. */
  const mailboxName = (id: string | null) =>
    id === null ? null : mailboxes?.find((m) => m.id === id)?.display_name ?? null
  const landingError = landing
    ? connectErrorText(landing.code, { ...landing.params, otherName: mailboxName(landing.other) })
    : null

  const listError = query.isError
    ? (query.error instanceof Error ? query.error.message : 'Failed to load mailboxes')
    : null
  const pageError = landingError ?? listError

  // SA-947 (spec P8): the providers the server offers.
  const providersQuery = useMailboxProviders()
  const providers = providersQuery.data ?? []
  const oauthProviders = providers.filter((p) => p.oauth_config_id !== null)
  const providersOffered = providersQuery.data ? oauthProviders.length > 0 : providersQuery.isError ? false : null
  const [connectFor, setConnectFor] = useState<Mailbox | null>(null)

  // SA-1387 (spec §5.4): an action's error and a refused delete belong to the mailbox they happened on.
  const [actionError, setActionError] = useState<{ id: string; message: string } | null>(null)
  const [deleteRefused, setDeleteRefused] = useState<{ mailbox: Mailbox; message: string } | null>(null)

  // Personas — reuse the app's persona hook/store (PersonasPage's pattern):
  // the store is populated lazily, so fetch on mount rather than assume
  // useAppInit already ran for this route.
  const { personas, fetchPersonas } = usePersonas()
  useEffect(() => {
    fetchPersonas()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Workflow picker options — the library endpoint already excludes
  // archived rows; drop the ones with no published version (the seed
  // path could not start them — SA-101).
  const workflowsQuery = useWorkflowsLibrary()
  const workflowOptions = (workflowsQuery.data ?? []).filter(
    (w) => w.current_version_id !== null,
  )

  const users = useAdminUsers()

  // ---- Provider OAuth client config ----
  const { configs: providerConfigs, update: updateProviderConfig } = useProviderConfigs(
    oauthProviders.map((p) => p.oauth_config_id as string),
  )
  const needsClient = providerNeedsClient(oauthProviders, providerConfigs)

  // SA-476 (spec D2): strict — no start is promised before the manifest says process_engine is on.
  const processEngine = useFeatureStore((s) => (s.isLoaded ? s.features.has('process_engine') : null))
  const setupOf = (mb: Mailbox) => sinkLabel(mb, workflowsQuery.data, processEngine)

  // ---- The pane the address names (spec §5.3) ----
  const pane = paneFrom(searchParams, mailboxes, providersOffered)
  const shown = pane.kind === 'mailbox' ? pane.mailbox : null
  const shownId = shown?.id ?? null
  // SA-1422 (spec §4.5): the banner offers its action only while the pane shows the mailbox it was for.
  const bannerAction = landing && landing.mailboxId !== null && landing.mailboxId === shownId
    ? refusalAction(landing.code, landing.params)
    : null
  // The mailbox shown before New mailbox, for its Cancel; and the one shown now, for a delete that finishes.
  const lastShownRef = useRef<string | null>(null)
  const shownIdRef = useRef<string | null>(null)
  useEffect(() => {
    shownIdRef.current = shownId
    if (shownId !== null) lastShownRef.current = shownId
  }, [shownId])
  /** Whether the pane still shows this mailbox, for a request that answers after the person may have moved on. */
  const stillShown = (mailbox: Mailbox) => shownIdRef.current === mailbox.id

  const containerRef = useRef<HTMLDivElement>(null)
  const paneRef = useRef<HTMLDivElement>(null)

  // SA-717 (spec T29), SA-1387: a mailbox that is not a router opens its editor with Use a router;
  // the id says which, so it never follows to another mailbox.
  const [editorOpen, setEditorOpen] = useState<string | null>(null)
  // Bumped after Cancel and a discard: the editor starts again from the stored policy. A save does not
  // bump it (SA-1398): the editor follows the saved policy itself and keeps anything typed meanwhile.
  const [editorKey, setEditorKey] = useState(0)
  const [editorError, setEditorError] = useState<string | null>(null)
  // Spec §5.7: the editor reports its unsaved edits; MailboxRouting reports false whenever it goes.
  const [routingDirty, setRoutingDirty] = useState(false)
  const routingEditorShown = pane.kind === 'mailbox' && pane.tab === 'routing'
    && (pane.mailbox.sink_type === 'router' || editorOpen === pane.mailbox.id)
  const handlersQuery = useMailboxHandlers(routingEditorShown)
  // Who last changed this mailbox's binding: every policy save is recorded.
  const changesQuery = useMailboxPolicyChanges(shownId, routingEditorShown)
  const agendasQuery = useIntakeAgendas(routingEditorShown)

  /** Every move of the pane; replace, never push, and every key the page does not own is kept. */
  const goTo = (target: PaneTarget) => {
    setSearchParams((prev) => withPane(prev, target), { replace: true })
    setEditorOpen(null)
    setEditorError(null)
    if (!(target.kind === 'mailbox' && target.id === shownId)) {
      setActionError(null)
      setDeleteRefused(null)
    }
  }

  /**
   * Spec §5.7: true when the routing editor holds nothing unsaved; else asks, and a yes discards the
   * draft (the editor starts again from the stored policy) and disarms the guard before the caller acts.
   */
  const confirmDiscard = () => {
    if (!routingDirty || shown === null) return true
    if (!window.confirm(discardRoutingConfirm(shown.display_name))) return false
    setRoutingDirty(false)
    setEditorKey((k) => k + 1)
    return true
  }

  // Leaving the page asks too: a reload or a closed tab through `beforeunload`, an in-app
  // navigation through the router's blocker, which the same confirm lets through or holds.
  const guard = useUnsavedChangesGuard({ when: routingDirty, shouldBlock: notAPageWrite })
  useEffect(() => {
    if (!guard.blocked) return
    if (confirmDiscard()) guard.proceed()
    else guard.reset()
    // Once per blocked navigation, with this render's answer: a later render would ask again.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guard.blocked])

  const stacked = () => {
    const el = containerRef.current
    if (!el) return false
    const rem = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16
    return el.getBoundingClientRect().width < STACK_BELOW_REM * rem
  }

  // Spec §5.5: stacked, the pane sits under the list, so a move the person asks for brings it into view.
  // It scrolls once the pane has drawn where it went: from an empty list, New mailbox draws the list above it.
  const revealRef = useRef(false)
  const revealPane = () => { revealRef.current = true }
  const paneKey = pane.kind === 'mailbox' ? `mailbox:${pane.mailbox.id}` : pane.kind
  useEffect(() => {
    if (!revealRef.current) return
    revealRef.current = false
    if (stacked()) paneRef.current?.scrollIntoView({ block: 'start' })
  }, [paneKey])

  const handleSelect = (id: string) => {
    if (id === shownId || !confirmDiscard()) return
    goTo({ kind: 'mailbox', id })
    revealPane()
  }

  const handleTab = (tab: MailboxTab) => {
    if (pane.kind !== 'mailbox' || tab === pane.tab || !confirmDiscard()) return
    goTo({ kind: 'mailbox', id: pane.mailbox.id, tab })
  }

  const showFirstRemaining = (deletedId: string) => {
    const remaining = (qc.getQueryData<Mailbox[]>(MAILBOXES_KEY) ?? []).filter((m) => m.id !== deletedId)
    qc.setQueryData<Mailbox[]>(MAILBOXES_KEY, (list) => list?.filter((m) => m.id !== deletedId))
    if (shownIdRef.current !== deletedId) return
    goTo(remaining[0] ? { kind: 'mailbox', id: remaining[0].id } : { kind: 'none' })
  }

  const handleCreated = (created: Mailbox) => {
    // Spec §5.3: the new mailbox is selected, ready to Connect; the list's refetch brings its counts.
    qc.setQueryData<Mailbox[]>(MAILBOXES_KEY, (list) =>
      list && !list.some((m) => m.id === created.id) ? [...list, created] : list)
    goTo({ kind: 'mailbox', id: created.id })
  }

  const handleCancelNew = () => {
    const list = mailboxes ?? []
    const back = list.find((m) => m.id === lastShownRef.current) ?? list[0]
    goTo(back ? { kind: 'mailbox', id: back.id } : { kind: 'none' })
  }

  /** SA-1080 (spec §5.2): `expected` is the stored policy the editor's review compared against. A save the
   * server refuses because the stored policy moved since answers `'policy_changed'` and shows no error:
   * the editor checks again and reopens the review. */
  const handleSaveRouting = async (
    mailbox: Mailbox, doc: PolicyDoc, expected: PolicyDoc,
  ): Promise<'policy_changed' | void> => {
    setEditorError(null)
    try {
      const updated = await updateMutation.mutateAsync({
        id: mailbox.id, sink_type: 'router', sink_config: doc, expected_policy: expected,
      })
      // The row takes the saved binding now, and the editor follows it when nothing was typed since the
      // send (SA-1398); the list's refetch brings the rest. Another mailbox's editor is left alone.
      qc.setQueryData<Mailbox[]>(MAILBOXES_KEY, (list) => list?.map((m) =>
        m.id === mailbox.id ? { ...m, sink_type: updated.sink_type, sink_config: updated.sink_config } : m))
      setEditorOpen((open) => (open === mailbox.id ? null : open))
    } catch (err) {
      if (isPolicyChanged(err)) return 'policy_changed'
      // Spec §5.4: a refusal that answers after the pane moved on is not shown on another mailbox.
      if (stillShown(mailbox)) setEditorError(errorMessage(err, 'Failed to save the routing policy'))
    }
  }

  const handleCancelRouting = (mailbox: Mailbox) => {
    if (!confirmDiscard()) return
    setEditorKey((k) => k + 1)
    setEditorError(null)
    if (mailbox.sink_type !== 'router') setEditorOpen(null)
  }

  // ---- Row actions ----
  // Spec §5.4: errors clear when the pane switches, so a failure that answers after it moved on is dropped.
  const fail = (mailbox: Mailbox, message: string) => {
    if (stillShown(mailbox)) setActionError({ id: mailbox.id, message })
  }
  const clearErrors = () => {
    setLanding(null)
    setActionError(null)
  }

  /** SA-1422: `account` names the account to sign in as (Change account, Connect as); absent, plain Connect. */
  const handleConnect = async (mailbox: Mailbox, account?: string) => {
    // Spec §5.7: an OAuth connect leaves the page. A yes disarms the guard here, before the await,
    // so leaving does not ask a second time. A group source links in the dialog and stays, so it asks nothing.
    if (!isGroupSource(mailbox) && !confirmDiscard()) return
    clearErrors()
    try {
      const result = await connectMutation.mutateAsync({ id: mailbox.id, account })
      // SA-947 (spec P10): a group source links in the dialog; only an OAuth
      // provider answers a consent URL to go to.
      if (isGroupSource(mailbox)) {
        setConnectFor(mailbox)
        return
      }
      if (result.authorization_url) window.location.assign(result.authorization_url)
    } catch (err) {
      fail(mailbox, errorMessage(err, 'Failed to start the connect flow'))
    }
  }

  /** SA-1422 (spec §4.4): the typed address's mail arrives in the connected account's inbox, as shown. */
  const handleConfirmAccount = async (mailbox: Mailbox) => {
    const account = mailbox.connected_account?.trim()
    if (!account) return
    clearErrors()
    try {
      await confirmMutation.mutateAsync({ id: mailbox.id, account })
    } catch (err) {
      const refusal = refusalDetail(err)
      if (refusal?.code === 'account_changed') {
        void qc.invalidateQueries({ queryKey: MAILBOXES_KEY })
        fail(mailbox, ACCOUNT_CHANGED)
      } else if (refusal?.code === 'account_in_use') {
        fail(mailbox, connectErrorText('account_in_use', { account, otherName: mailboxName(refusal.other) }))
      } else {
        fail(mailbox, errorMessage(err, CONFIRM_FAILED))
      }
    }
  }

  const handleBannerAction = () => {
    if (!shown || !bannerAction) return
    if (bannerAction.kind === 'connect_as') void handleConnect(shown, bannerAction.account)
    else setChangeFor({ mailbox: shown, initialAccount: bannerAction.account })
  }

  const handlePollNow = async (mailbox: Mailbox) => {
    clearErrors()
    try {
      const result = await pollMutation.mutateAsync(mailbox.id)
      // SA-102: non-ok outcomes (cursor re-anchor today) ride the 200 —
      // surface the server's detail in the same banner the 502s use.
      // SA-108: an outcome without a detail still gets a banner naming it.
      if (result.outcome !== 'ok') {
        fail(mailbox, result.detail || `Poll finished with outcome "${result.outcome}"`)
      }
    } catch (err) {
      fail(mailbox, errorMessage(err, 'Poll failed — the mailbox may need to be reconnected'))
    }
  }

  const handleTogglePause = async (mailbox: Mailbox) => {
    clearErrors()
    try {
      if (mailbox.status === 'active' || mailbox.status === 'reauth_required') await pauseMutation.mutateAsync(mailbox.id)
      else if (mailbox.status === 'paused') await resumeMutation.mutateAsync(mailbox.id)
    } catch (err) {
      fail(mailbox, errorMessage(err, 'Failed to update mailbox'))
    }
  }

  const handleCancelBackload = async (mailbox: Mailbox) => {
    if (!mailbox.backload) return
    clearErrors()
    try {
      await cancelBackloadMutation.mutateAsync({ id: mailbox.id, jobId: mailbox.backload.id })
    } catch (err) {
      fail(mailbox, backloadErrorText(err))
    }
  }

  const handleToggleOutbound = async (mailbox: Mailbox, outboundEnabled: boolean) => {
    clearErrors()
    try {
      await updateMutation.mutateAsync({ id: mailbox.id, outbound_enabled: outboundEnabled })
    } catch (err) {
      fail(mailbox, errorMessage(err, 'Failed to update mailbox'))
    }
  }

  const handleDelete = async (mailbox: Mailbox) => {
    const group = isGroupSource(mailbox)
    const ask = group ? deleteConfirmText(mailbox) : emailDeleteConfirm(mailbox.display_name)
    if (!window.confirm(ask)) return
    clearErrors()
    setDeleteRefused(null)
    try {
      await deleteMutation.mutateAsync({ id: mailbox.id })
      showFirstRemaining(mailbox.id)
    } catch (err) {
      const message = errorMessage(err, 'Failed to delete mailbox')
      // SA-947 (spec P10): WAHA refused, so nothing was deleted; the person may
      // delete the row and leave the session.
      if (group && err instanceof ApiError && err.status >= 500) {
        if (stillShown(mailbox)) setDeleteRefused({ mailbox, message })
      } else fail(mailbox, message)
    }
  }

  const handleDeleteAnyway = async () => {
    if (!deleteRefused || !window.confirm(DELETE_ANYWAY_CONFIRM)) return
    const { mailbox } = deleteRefused
    setDeleteRefused(null)
    try {
      await deleteMutation.mutateAsync({ id: mailbox.id, keepSession: true })
      showFirstRemaining(mailbox.id)
    } catch (err) {
      fail(mailbox, errorMessage(err, 'Failed to delete mailbox'))
    }
  }

  const nameOf = (id: string | null, find: (id: string) => string | undefined) =>
    id === null ? null : find(id) ?? UNKNOWN
  const listShown = mailboxes !== undefined && (mailboxes.length > 0 || pane.kind === 'new')

  const paneBody = () => {
    if (pane.kind === 'new') {
      return (
        <div className="space-y-4 p-4">
          <NewMailboxForm
            providers={providers}
            providersFailed={providersQuery.isError}
            personas={personas}
            users={users}
            workflows={workflowsQuery.data}
            workflowsFailed={workflowsQuery.isError}
            onCreated={handleCreated}
            onCancel={handleCancelNew}
          />
        </div>
      )
    }
    if (pane.kind === 'providers') {
      return (
        <div className="space-y-4 p-4">
          <ProviderSettings providers={oauthProviders} configs={providerConfigs} onSaved={updateProviderConfig} />
        </div>
      )
    }
    if (pane.kind === 'empty') {
      return (
        <div className="flex flex-col items-center gap-3 px-4 py-12 text-center text-muted-foreground">
          <Mail className="w-8 h-8" />
          <p>{EMPTY_LIST}</p>
          <Button onClick={() => { goTo({ kind: 'new' }); revealPane() }} leftIcon={<Plus className="w-4 h-4" />}>
            {NEW_MAILBOX}
          </Button>
        </div>
      )
    }
    if (pane.kind !== 'mailbox') return null
    const mb = pane.mailbox
    return (
      <MailboxPane
        mailbox={mb}
        tab={pane.tab}
        providers={providers}
        actionError={actionError?.id === mb.id ? actionError.message : null}
        deleteRefused={deleteRefused?.mailbox.id === mb.id ? deleteRefused.message : null}
        onTab={handleTab}
        onConnect={() => handleConnect(mb)}
        onChangeAccount={() => setChangeFor({ mailbox: mb })}
        onConfirmAccount={() => handleConfirmAccount(mb)}
        onLinkGroup={() => setConnectFor(mb)}
        onTogglePause={() => handleTogglePause(mb)}
        onPollNow={() => handlePollNow(mb)}
        onDelete={() => handleDelete(mb)}
        onDeleteAnyway={handleDeleteAnyway}
      >
        {pane.tab === 'overview' && (
          <MailboxOverview
            mailbox={mb}
            setup={setupOf(mb)}
            personaName={nameOf(mb.persona_id, (id) => personas.find((p) => p.id === id)?.name)}
            ownerName={nameOf(mb.owner_user_id, (id) => {
              const user = users.find((u) => u.id === id)
              return user ? user.display_name || user.email : undefined
            })}
            onEditRouting={() => goTo({ kind: 'mailbox', id: mb.id, tab: 'routing' })}
            onUseRouter={() => {
              goTo({ kind: 'mailbox', id: mb.id, tab: 'routing' })
              setEditorOpen(mb.id)
            }}
            onLoadEarlier={() => setBackloadFor(mb)}
            onCancelLoad={() => handleCancelBackload(mb)}
            onToggleOutbound={(on) => handleToggleOutbound(mb, on)}
          />
        )}
        {pane.tab === 'routing' && (
          <MailboxRouting
            mailbox={mb}
            editorOpen={editorOpen === mb.id}
            editorKey={editorKey}
            setup={setupOf(mb)}
            handlers={handlersQuery.data ?? []}
            agendas={agendasQuery.data ?? []}
            workflows={workflowOptions}
            lastChange={changesQuery.data?.[0] ?? null}
            switchWarning={mb.sink_type === 'router' ? null
              : `Saving switches this mailbox from ${setupOf(mb)} to a router. Until it has kinds, every message waits for a person.`}
            error={editorError}
            saving={updateMutation.isPending}
            onOpenEditor={() => { setEditorError(null); setEditorOpen(mb.id) }}
            onPreview={(doc) => mailboxesService.previewPolicy(mb.id, doc)}
            onSave={(doc, expected) => handleSaveRouting(mb, doc, expected)}
            onCancel={() => handleCancelRouting(mb)}
            onDirtyChange={setRoutingDirty}
          />
        )}
        {pane.tab === 'triage' && <TriageMetricsPanel mailboxId={mb.id} />}
      </MailboxPane>
    )
  }

  return (
    <PageFrame width="list" className="space-y-6">
      {guard.element}
      <PageHeader
        title="Mailboxes"
        icon={Mail}
        subtitle="Ingest a group inbox into quote records, workflow conversations or staff conversations, or let a router decide what each email is. Email is inbound-only."
        actions={
          <>
            {oauthProviders.length > 0 && (
              <Button
                variant="secondary"
                {...tourAnchor('mailboxes.header.providers')}
                aria-label={needsClient ? PROVIDER_SETTINGS_MISSING : undefined}
                onClick={() => {
                  if (pane.kind === 'providers' || !confirmDiscard()) return
                  goTo({ kind: 'providers' })
                  revealPane()
                }}
              >
                {PROVIDER_SETTINGS}
                {needsClient && <span aria-hidden="true" className="ml-1.5 inline-block size-2 rounded-full bg-warning" />}
              </Button>
            )}
            <Button
              {...tourAnchor('mailboxes.header.new')}
              onClick={() => {
                if (pane.kind === 'new' || !confirmDiscard()) return
                goTo({ kind: 'new' })
                revealPane()
              }}
              leftIcon={<Plus className="w-4 h-4" />}
            >
              {NEW_MAILBOX}
            </Button>
          </>
        }
      />

      {pageError && (
        <Alert variant="destructive">
          {bannerAction ? (
            <AlertDescription className="flex flex-wrap items-center gap-2">
              <span>{pageError}</span>
              <Button variant="outline" size="sm" onClick={handleBannerAction}>
                {bannerAction.kind === 'connect_as' ? connectAsLabel(bannerAction.account) : CHANGE_ACCOUNT}
              </Button>
            </AlertDescription>
          ) : (
            <AlertDescription>{pageError}</AlertDescription>
          )}
        </Alert>
      )}

      {pane.kind === 'loading' ? (
        query.isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Spinner size="lg" />
          </div>
        ) : null
      ) : (
        <div ref={containerRef} className="@container">
          <div className={cn('grid grid-cols-1 gap-4', listShown && '@[40rem]:grid-cols-[14rem_minmax(0,1fr)]')}>
            {listShown && (
              <MailboxList
                groups={groupMailboxes(mailboxes ?? [])}
                selectedId={shownId}
                creating={pane.kind === 'new'}
                lineFor={(mb) => attentionLine(mb, new Date(), setupOf(mb))}
                onSelect={handleSelect}
              />
            )}
            <div
              ref={paneRef}
              data-testid="mailbox-pane"
              className="min-w-0 scroll-mt-4 rounded-lg border border-section-border bg-card shadow-xs"
            >
              {paneBody()}
            </div>
          </div>
        </div>
      )}
      {backloadFor && (
        <BackloadDialog
          key={backloadFor.id}
          mailbox={backloadFor}
          open
          onOpenChange={(open) => { if (!open) setBackloadFor(null) }}
        />
      )}
      {changeFor && (
        <ChangeAccountDialog
          key={changeFor.mailbox.id}
          mailbox={changeFor.mailbox}
          open
          initialAccount={changeFor.initialAccount}
          onOpenChange={(open) => { if (!open) setChangeFor(null) }}
          onContinue={(account) => {
            const { mailbox } = changeFor
            setChangeFor(null)
            void handleConnect(mailbox, account)
          }}
        />
      )}
      {connectFor && (
        <WahaConnectDialog
          key={connectFor.id}
          mailbox={connectFor}
          open
          onOpenChange={(open) => { if (!open) setConnectFor(null) }}
        />
      )}
    </PageFrame>
  )
}
